import { useState } from 'react'

import { IconPlus } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'

import type { UserBank } from '@subaio/api-types/psd2'
import type { AsyncResource } from '@subaio/components'
import { handleLogin, Psd2Error } from '@subaio/psd2login-web'
import { cases } from '@subaio/utils'

import type { Step, StepType } from 'containers/Flow/Step'
import { UserType } from 'containers/Flow/stepHandler'

import { ButtonSolid } from 'components/Buttons'
import { Card } from 'components/Layout'
import { List } from 'components/Lists'

import { ButtonGroup } from '../ButtonGroup'
import { Container } from '../Container'
import { DefaultErrorSteps } from '../DefaultErrorSteps'
import { InfoBox } from '../InfoBox'
import { Wrapper } from '../Wrapper'
import { ItemBank } from './ItemBank'
import { ItemIcon } from './ItemIcon'
import { Psd2loginError } from './Psd2loginErorr'

type Props = TaggedType<Step, StepType.Accounts> & {
	selectedBanksResource: AsyncResource<Array<UserBank> | null>
	urlResource: AsyncResource<string | null>

	onRefresh: () => void
	onNext: (budgetId: string) => void
}

export const Accounts = ({
	isBankDataReceived,
	urlResource,
	budgetId,
	selectedBanksResource,
	userMetadata,
	onNext,
	onRefresh,
}: Props) => {
	const [psd2Status, setPsd2Status] = useState<'ready' | 'success' | Psd2Error>('ready')
	const { applicantName, coapplicantName, userType } = userMetadata

	return (
		<Wrapper>
			<Card>
				<Container>
					<h1>
						<FormattedMessage id='flow.accounts.title' defaultMessage='Add your accounts and credit cards' />
					</h1>
					<p>
						{cases(userType, {
							[UserType.Default]: (
								<FormattedMessage
									id='flow.accounts.descriptionOne'
									defaultMessage='In order to get a complete picture of your finances, it is important that you add all your banks and accounts. Also remember credit cards that you do not have through your bank.'
								/>
							),
							[UserType.PrimaryWithCoapplicant]: (
								<FormattedMessage
									id='flow.accounts.descriptionOneWithCoapplicant'
									defaultMessage="To get a complete picture of your finances, it's important that you add all your financial institutions and accounts, including accounts you have with {coapplicantName}. Don't forget any credit cards that you don't have through your bank."
									values={{ coapplicantName }}
								/>
							),
							[UserType.Coapplicant]: (
								<FormattedMessage
									id='flow.accounts.descriptionOneCoapplicant'
									defaultMessage="To get a complete picture of your finances, it's important that you add all your financial institutions and accounts, including accounts you have with {applicantName}. Don't forget any credit cards that you don't have through your bank."
									values={{ applicantName }}
								/>
							),
						})}
					</p>
					<p>
						<FormattedMessage
							id='flow.accounts.descriptionTwo'
							defaultMessage='Click on "Add bank" and check that all accounts that reflect your personal finances are included. If you have accounts that do not relate to your private finances, such as association accounts or company accounts, these can be de-selected.'
						/>
					</p>
					<p>
						<FormattedMessage
							id='flow.accounts.descriptionThree'
							defaultMessage='Once you have added all your accounts and credit cards, press “Continue”.'
						/>
					</p>
				</Container>
				<h3>
					<FormattedMessage id='flow.accounts.listTitle' defaultMessage='Your selected banks' />
				</h3>
				<List>
					{/* We show the banks as loading while waiting for both the URL and the selected banks to be available */}
					{(selectedBanksResource.status === 'loading' || urlResource.status === 'loading') && (
						<ItemBank title='' imageUrl={null} isLoading />
					)}
					{/* We inform the user that we cannot get their selected banks if that is the only resource that fails */}
					{selectedBanksResource.status === 'errored' && urlResource.status === 'done' && (
						<ItemIcon
							type='warning'
							title={
								<FormattedMessage
									id='flow.accounts.showAccountsError'
									defaultMessage='We cannot show you your already added financial institutions at this time'
								/>
							}
							subtitle={
								<FormattedMessage
									id='flow.accounts.showAccountsErrorDescription'
									defaultMessage='You can try to continue by adding your financial institutions below if possible'
								/>
							}
						/>
					)}
					{selectedBanksResource.status === 'done' &&
						selectedBanksResource.value?.map((bank) => <ItemBank {...bank} />)}
					{/* We inform the user that we cannot add any more banks if that is the only resource that fails */}
					{urlResource.status === 'errored' && selectedBanksResource.status === 'done' && (
						<ItemIcon
							type='danger'
							title={
								<FormattedMessage
									id='flow.accounts.addAccountsError'
									defaultMessage='We cannot add any financial institutions at this time'
								/>
							}
						/>
					)}
					{/* If both resources error out, we inform the user that we cannot really do anything at this time */}
					{urlResource.status === 'errored' && selectedBanksResource.status === 'errored' && (
						<ItemIcon
							type='danger'
							title={
								<FormattedMessage
									id='flow.accounts.addAccountsError'
									defaultMessage='We cannot add any financial institutions at this time'
								/>
							}
						/>
					)}
					{selectedBanksResource.status !== 'loading' &&
						urlResource.status === 'done' &&
						urlResource.value !== null && (
							<ItemIcon
								title={
									<FormattedMessage id='flow.accounts.buttonAddAccounts' defaultMessage='Add financial institutes' />
								}
								icon={<IconPlus />}
								onClick={async () => {
									try {
										await handleLogin({ url: urlResource.value! })
										setPsd2Status('success')
										onRefresh()
									} catch (error) {
										onRefresh()
										if (error instanceof Psd2Error) {
											console.error(error)
											return setPsd2Status(error)
										}
										throw error
									}
								}}
							/>
						)}
				</List>
				{psd2Status instanceof Psd2Error && (
					<InfoBox type='danger'>
						<div>
							<p>
								<Psd2loginError type={psd2Status.type} />
							</p>
							<DefaultErrorSteps />
						</div>
					</InfoBox>
				)}
				{urlResource.status === 'errored' && selectedBanksResource.status !== 'loading' && (
					<InfoBox type='danger'>
						<div>
							<DefaultErrorSteps />
						</div>
					</InfoBox>
				)}
				{psd2Status === 'ready' &&
					selectedBanksResource.value &&
					selectedBanksResource.value.length > 0 &&
					urlResource.status !== 'errored' &&
					isBankDataReceived !== true && (
						<InfoBox type='warning'>
							<FormattedMessage
								id='flow.accounts.addedBanksPreviously'
								defaultMessage='You have previously added your financial institutes, but you did not send them to us yet. Please re-open the flow by pressing "Add financial institutes" and submit your financial institutes by pressing "Continue" in the flow to continue, or add more if any are missing.'
							/>
						</InfoBox>
					)}
				<ButtonGroup>
					<ButtonSolid
						label={<FormattedMessage id='flow.buttonNext' defaultMessage='Continue' />}
						disabled={psd2Status !== 'success' && isBankDataReceived === false}
						onClick={() => onNext(budgetId)}
					/>
				</ButtonGroup>
			</Card>
		</Wrapper>
	)
}
